import {AfterViewInit, ApplicationRef, Component, inject, OnInit, signal, TemplateRef, ViewChild, WritableSignal} from '@angular/core';
import {
  ListTableFiltersWrapperParent
} from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper-parent';
import {IMsfFormBuilder} from "@root/src/app/components/form-builder/form-builder";
import {CropService} from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import {FieldService, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {ButtonDirective} from 'primeng/button';
import {CropsNameColors} from '@app/pages/electronic-field-map/components/crops-name-colors/crops-name-colors';
import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {ListTableFiltersWrapper} from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper';
import {MsfNumber} from '@app/utils/pipes/format-number';
import {ServerTranslatePipe} from '@app/utils/pipes/server-translate-pipe';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Tooltip} from 'primeng/tooltip';
import {TableColumn} from '@components/data-table/data-table.component';
import {DatePipe, NgIf} from '@angular/common';
import {Router} from '@angular/router';
import {
  CropIdentificationExtendedDto
} from '@6grain/masofa-monolith-api-client/ai-inference/model/cropIdentificationExtendedDto';
import {
  CropIdentificationService
} from '@6grain/masofa-monolith-api-client/ai-inference/api/cropIdentification.service';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {buildFilterQuery} from '@app/utils/_msf';
import {FilterOperator} from '@app/utils/filter-operator';
import {CropCacheService} from '@services/caches/crop-cache.service';
import {FieldPassportLinkComponent} from '@components/field-passport-link/field-passport-link.component';
import {RegionDisplay} from '@components/region-display/region-display';
import {DialogService} from 'primeng/dynamicdialog';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {BidWorkTypeService} from '@6grain/masofa-monolith-api-client/dictionaries/api/bidWorkType.service';
import {openCropIdentificationCreateRequestDialog} from '@app/pages/crop-identification/request/crop-identification-create-request.util';
import {AI_INFERENCE_IMAGE_DATA_SOURCE_USES} from '@app/shared/data/ai-inference-image-data-source-uses-data';
import { LocalizeValuePipe } from '@app/utils/pipes/localize-value.pipe';

@Component({
  selector: 'app-crop-identification-list',
  standalone: true,
  templateUrl: './crop-identification-list.html',
  imports: [
    ButtonDirective,
    CropsNameColors,
    ListTableFiltersWrapper,
    MsfNumber,
    ServerTranslatePipe,
    TranslatePipe,
    Tooltip,
    NgIf,
    FieldPassportLinkComponent,
    RegionDisplay,
    DateFormatLangPipe,
    LocalizeValuePipe
],
  styleUrl: './crop-identification-list.scss',
  providers: [
    DateFormatLangPipe,
    DatePipe,
    DialogService,
  ]
})
export class CropIdentificationList extends ListTableFiltersWrapperParent<CropIdentificationExtendedDto, CropIdentificationService> implements OnInit, AfterViewInit {
  override service: CropIdentificationService = inject(CropIdentificationService);
  override getAllMethod: string = 'aiInferenceCropIdentificationGetExtendedListPost';
  override getTotalMethod: string | undefined = 'aiInferenceCropIdentificationGetTotalCountPost';
  override storageKey: string = '';
  override filtersForms: IMsfFormBuilder[] = [];


  @ViewChild('actionsTemplate', {static: true}) actionsTemplate!: TemplateRef<unknown>;
  @ViewChild('regionTemplate', {static: true}) regionTemplate!: TemplateRef<unknown>;
  @ViewChild('plannedCropTemplate', {static: true}) plannedCropTemplate!: TemplateRef<unknown>;
  @ViewChild('fieldTemplate', {static: true}) fieldTemplate!: TemplateRef<unknown>;
  @ViewChild('sourceTemplate', {static: true}) sourceTemplate!: TemplateRef<unknown>;
  @ViewChild('areaTemplate', {static: true}) areaTemplate!: TemplateRef<unknown>;
  @ViewChild('culture1Template', {static: true}) culture1Template!: TemplateRef<unknown>;
  @ViewChild('accuracy1Template', {static: true}) accuracy1Template!: TemplateRef<unknown>;
  @ViewChild('culture2Template', {static: true}) culture2Template!: TemplateRef<unknown>;
  @ViewChild('accuracy2Template', {static: true}) accuracy2Template!: TemplateRef<unknown>;
  @ViewChild('dateTemplate', {static: true}) dateTemplate!: TemplateRef<unknown>;

  regionService = inject(RegionManagerService);
  cropService = inject(CropService);
  cropCacheService = inject(CropCacheService);
  fieldService = inject(FieldService);
  seasonService = inject(SeasonService);
  bidService = inject(BidService);
  bidWorkTypeService = inject(BidWorkTypeService);
  dialogService = inject(DialogService);
  translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly appRef = inject(ApplicationRef);
  private readonly datePipe = inject(DatePipe);
  isExporting = signal(false);
  tableColumns: WritableSignal<TableColumn[]> = signal([]);

  /** Same pattern as passport menus (`getDefaultFilter` + `filtersParams` on init). */
  protected getDefaultFilter(): Object[] {
    return [
      {
        filterField: 'topProbability',
        filterOperator: FilterOperator.GreaterThan,
        filterValue: 0.59,
      },
    ];
  }

  override ngOnInit() {
    super.ngOnInit();
    this.filtersParams = this.getDefaultFilter();
  }

  ngAfterViewInit() {
    this.cropCacheService.getCrops().subscribe();
    this.initFiltersForm();
    this.initTableColumns();
  }

  getFieldName(row: CropIdentificationExtendedDto | Record<string, unknown>): string {
    const r = row as { field?: { name?: string } };
    return r?.field?.name ?? '';
  }

  getFieldId(row: CropIdentificationExtendedDto | Record<string, unknown>): string {
    const r = row as { field?: { id?: string }; fieldId?: string };
    return r?.field?.id ?? r?.fieldId ?? '';
  }

  /** Same resolution as field-list / requests table (`region` nested object vs `regionId`). */
  getRegionIdForRow(row: CropIdentificationExtendedDto | Record<string, unknown>): string | null {
    const r = row as { region?: { id?: string }; regionId?: string };
    const id = r?.region?.id ?? r?.regionId;
    return id ? String(id) : null;
  }

  getFieldDetailsUrl(row: CropIdentificationExtendedDto | Record<string, unknown>): string {
    const fieldId = row.fieldId
    if (!fieldId) {
      return '';
    }
    return `${window.location.origin}/electronic-field-map/fields/${fieldId}/general`;
  }

  /** Get localized source name from DTO. */
  getSourceLabel(row: CropIdentificationExtendedDto | Record<string, unknown>): string {
    const r = row as { aiInferenceImageDataSourceNames?: Record<string, string> };
    const names = r?.aiInferenceImageDataSourceNames;
    if (!names) {
      return '';
    }
    const lang = this.translateService.currentLang || 'ru-RU';
    return names[lang] ?? names['ru-RU'] ?? '';
  }

  private formatFilterModelForQuery(model: Record<string, Object>): Record<string, Object> {
    const filters = {...model};
    const raw = filters['identificationDate'];
    if (raw == null || raw === '') {
      return filters;
    }
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) {
      filters['identificationDate'] = raw.trim();
      return filters;
    }
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
      filters['identificationDate'] = this.datePipe.transform(raw, 'yyyy-MM-dd') ?? '';
      return filters;
    }
    return filters;
  }

  protected override async applyFilters() {
    const filters = this.formatFilterModelForQuery({...this.filterModel});
    const fromForm = buildFilterQuery(filters);
    this.filtersParams = [...this.getDefaultFilter(), ...fromForm];
    await this.regionService.includeChildInRequest(
      this.filterModel as {regionId?: string | null},
      this.filtersParams as Array<{filterField?: string | null; filterOperator?: number; filterValue?: unknown}>
    );
    this.toggleFilter = !this.toggleFilter;
  }

  protected override clearFilters() {
    this.filterModel = {};
    this.toggleFilter = !this.toggleFilter;
    this.filtersParams = this.getDefaultFilter();
  }

  initFiltersForm(): void {
    this.filtersForms = [
      {
        name: 'identificationDate',
        title: 'Date',
        type: 'date',
        options: {
          date: {dateOnly: true},
        },
      },
      {
        name: 'fieldId',
        title: 'Field name',
        type: 'select-paginate',
        selectService: this.fieldService,
        selectServiceMethod: 'cropMonitoringFieldGetByQueryPost',
        placeholder: 'Enter field name',
        options: {
          select: {
            optionLabel: 'name',
            optionValue: 'id',
            searchBy: 'name',
            translateBy: 'none'
          }
        },
      },
      {
        name: 'CropId',
        title: 'Culture',
        type: 'select-paginate',
        selectService: this.cropService,
        selectServiceMethod: 'dictionariesCropGetByQueryPost',
        options: {
          select: {
            searchBy: 'names',
          }
        },
      },
      {
        title: 'Region',
        name: 'regionId',
        type: 'tree-select',
        selectService: this.regionService,
        selectServiceMethod: 'getRegionsObservable',
        options: {
          treeSelect: {
            treeBy: 'parentId',
          }
        },
        placeholder: 'Select region',
      },
      {
        name: 'source',
        title: 'CROP_IDENTIFICATION_AI_DETECTION',
        type: 'select',
        placeholder: 'Select source',
        selectOptions: AI_INFERENCE_IMAGE_DATA_SOURCE_USES.map(item => ({
          name: item.names[this.translateService.currentLang] || item.names['ru-RU'] || item.names['en-US'],
          id: item.id,
        })),
        options: {
          clearable: false,
          select: {optionLabel: 'name', translateBy: 'none'}
        }
      },
    ];
  }


  initTableColumns() {
    this.tableColumns.set([
      {
        field: 'identificationDate',
        header: 'DATE_TIME',
        type: 'custom',
        customTemplate: this.dateTemplate,
      },
      {
        field: 'regionId',
        header: 'REGION_RAYON',
        type: 'custom',
        customTemplate: this.regionTemplate
      },
      {
        field: 'plannedCropId',
        header: 'PLANNED_CROP',
        type: 'custom',
        customTemplate: this.plannedCropTemplate
      },
      {
        field: 'culture',
        header: 'CULTURE_1',
        type: 'custom',
        customTemplate: this.culture1Template
      },
      {
        field: 'accuracy',
        header: 'ACCURACY_1',
        type: 'custom',
        customTemplate: this.accuracy1Template
      },
      {
        field: 'culture',
        header: 'CULTURE_2',
        type: 'custom',
        customTemplate: this.culture2Template
      },
      {
        field: 'accuracy',
        header: 'ACCURACY_2',
        type: 'custom',
        customTemplate: this.accuracy2Template
      },
      {
        field: 'fieldId',
        header: 'Field',
        type: 'custom',
        customTemplate: this.fieldTemplate
      },
      {
        field: 'source',
        header: 'CROP_IDENTIFICATION_AI_DETECTION',
        type: 'custom',
        customTemplate: this.sourceTemplate
      },
      {
        field: 'fieldArea',
        header: 'Area of field, ha',
        width: '150px',
        type: 'custom',
        customTemplate: this.areaTemplate
      },
      {
        field: '',
        header: '',
        type: 'custom',
        customTemplate: this.actionsTemplate,
        width: '120px',
      },
    ])
  }

  openOnGis(row: CropIdentificationExtendedDto | Record<string, unknown>, event?: Event): void {
    event?.stopPropagation();
    const id = (row as { id?: string })?.id;
    if (!id) {
      return;
    }
    const fieldId = this.getFieldId(row);
    this.router.navigate(['/crop-identification/gis'], {
      queryParams: {
        focusIdentificationId: id,
        ...(fieldId ? {focusFieldId: fieldId} : {}),
      },
    });
  }

  openCreateRequest(row: CropIdentificationExtendedDto | Record<string, unknown>, event?: Event): void {
    event?.stopPropagation();
    const fieldId = this.getFieldId(row);
    if (!fieldId) {
      return;
    }

    const r = row as { topCandidates?: Array<{id?: string}> };
    const candidate1 = r?.topCandidates?.[0];
    openCropIdentificationCreateRequestDialog(
      {
        fieldId,
        cropId: candidate1?.id ?? (row as {cropId?: string | null})?.cropId ?? null,
        preferredCrop: undefined, // Names now come from backend, cache lookup not needed
        lat: this.getLatForRequest(row),
        lng: this.getLngForRequest(row),
      },
      {
        dialogService: this.dialogService,
        bidService: this.bidService,
        bidWorkTypeService: this.bidWorkTypeService,
        fieldService: this.fieldService,
        seasonService: this.seasonService,
        datePipe: this.datePipe,
        translate: this.translateService,
        appRef: this.appRef,
      },
    );
  }

  private getLatForRequest(row: CropIdentificationExtendedDto | Record<string, unknown>): number | null {
    const raw =
      (row as { latitude?: number | string | null }).latitude ??
      (row as { centerY?: number | string | null }).centerY;
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  }

  private getLngForRequest(row: CropIdentificationExtendedDto | Record<string, unknown>): number | null {
    const raw =
      (row as { longitude?: number | string | null }).longitude ??
      (row as { centerX?: number | string | null }).centerX;
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  }

  loadLinkedModelsData(data: CropIdentificationExtendedDto[]) {
    // Names are now provided directly by backend (`field`, `user`, `region`, `crop`).
  }

  async onExport() {
    this.isExporting.set(true);
    const fromForm = buildFilterQuery(this.formatFilterModelForQuery({...this.filterModel}));
    const filters = [...this.getDefaultFilter(), ...fromForm];
    await this.regionService.includeChildInRequest(
      this.filterModel as {regionId?: string | null},
      filters as Array<{filterField?: string | null; filterOperator?: number; filterValue?: unknown}>
    );

    this.service.aiInferenceCropIdentificationExportToExcelPost({filters}).subscribe({
      next: () => this.isExporting.set(false),
      error: () => this.isExporting.set(false),
    });
  }
}
